import React, { useEffect, useState } from "react";
import Masonry from "react-masonry-component";
import i18next from "i18next";
import Select from "react-select";

import { PageTitle } from "../../PageTitle";
import { ReportDataHelper, debounce } from "../../../helpers";
import { DashboardService } from "../../../services";
import { TextInput } from "../../TextInput";

import config from "config";

const MASONRY_OPTIONS = {
  itemSelector: ".masonry-item",
  transitionDuration: 0,
  percentPosition: true,
  columnWidth: ".masonry-sizer",
  gutter: ".gutter-sizer",
  stagger: 30,
};

const NeedsAttentionCard = ({
  location,
  counts,
  locationClicked,
  needsAttention,
}) => {
  return (
    <div className="dash-card-region">
      <div
        className={`card-region__header locations__header home-dash${
          needsAttention ? " critical" : ""
        }`}
      >
        <div
          className={`location-name`}
          style={{ cursor: "pointer" }}
          onClick={() => {
            locationClicked(location);
          }}
        >
          {location.label}
        </div>
      </div>

      <div className="card-region__cards">
        <div>
          {location.grouping?.parent?.parent && (
            <div className="card-location-header">
              <span>
                <img src={`${config.WEB_BASE_URL}/images/icon/black-map-marker.png`} />
                {location.grouping.parent.parent.parent ? (
                  <div>
                    <span>{location.grouping.parent.parent.name}</span>{" "}
                    <FontAwesomeIcon icon={faChevronRight} size="sm" />{" "}
                    {location.grouping.parent.name}
                  </div>
                ) : (
                  <div>{location.grouping.parent.name}</div>
                )}
              </span>
            </div>
          )}
          <div className="other-info">
            <div className={counts.criticalCount > 0 ? "critical" : undefined}>
              <span>
                {i18next.t(
                  "pages.locations-needing-attention.event-type-critical"
                )}{" "}
              </span>
              <span className="dots"></span>
              <span> {counts.criticalCount}</span>
            </div>
          </div>
          <div className="other-info">
            <div>
              <span>
                {i18next.t(
                  "pages.locations-needing-attention.event-type-warning"
                )}{" "}
              </span>
              <span className="dots"></span>
              <span> {counts.warningCount}</span>
            </div>
          </div>
          <div className="other-info">
            <div>
              <span>
                {i18next.t("pages.locations-needing-attention.other.info")}{" "}
              </span>
              <span className="dots"></span>
              <span> {counts.informationCount}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const findEventLevelForLocationId = (
  eventCountsByLocationAndLevel,
  eventLevel,
  locationId
) => {
  var eventCountsForLocation = null;

  eventCountsByLocationAndLevel.forEach((entry) => {
    if (entry.locationId == locationId && entry.eventLevel == eventLevel) {
      eventCountsForLocation = entry;
    }
  });

  if (eventCountsForLocation == null) {
    return 0;
  } else {
    return eventCountsForLocation.eventLevelCount;
  }
};

const OtherLocationsArea = ({
  availableLocations,
  offlineLocations,
  locationClicked,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [locationReportData, setLocationReportData] = useState([]);
  const [eventLevelToken, setEventLevelToken] = useState("needs-attention");
  const [orderBy, setOrderBy] = useState({
    label: "Alphabetical A-Z",
    value: "alpha-asc",
  });
  const [stringFilter, setStringFilter] = useState("");

  useEffect(() => {
    setIsLoading(true);
    setLocationReportData([]);
    if (availableLocations && availableLocations.length > 0) {
      DashboardService.getReportOrDashboardDataByToken(
        "location_event_count_by_level_report"
      )
        .then((response) => {
          if (response.reportResult) {
            const eventCountsByLocationAndLevel =
              ReportDataHelper.convertQueryResponse(response.reportResult);
            let data = [];
            let locationsToDisplay =
              eventLevelToken === "locations_offline_count"
                ? availableLocations.filter((location) =>
                    offlineLocations.some(
                      (offlineLocation) =>
                        location.value === offlineLocation.location_id
                    )
                  )
                : availableLocations;

            locationsToDisplay.filter((location) =>
            location.label.toLowerCase().includes(stringFilter.toLowerCase())
          ).forEach((location) => {
              const entry = {
                location: location,
                criticalCount: findEventLevelForLocationId(
                  eventCountsByLocationAndLevel,
                  "critical",
                  location.value
                ),
                warningCount: findEventLevelForLocationId(
                  eventCountsByLocationAndLevel,
                  "warning",
                  location.value
                ),
                informationCount: findEventLevelForLocationId(
                  eventCountsByLocationAndLevel,
                  "information",
                  location.value
                ),
              };
              if (eventLevelToken === "needs-attention") {
                if (entry.criticalCount || entry.warningCount) data.push(entry);
              } else if (eventLevelToken === "all-others") {
                if (
                  !entry.criticalCount &&
                  !entry.warningCount &&
                  !offlineLocations.some(
                    (offlineLocation) =>
                      entry.location.value === offlineLocation.location_id
                  )
                )
                  data.push(entry);
              } else {
                data.push(entry);
              }
            });
            data = data.sort((entryA, entryB) => entryA.location.label.localeCompare(entryB.location.label))
            if (orderBy.value === "alpha-desc") data = data.reverse();
            setLocationReportData(data);
          } else {
            setLocationReportData([]);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setLocationReportData([]);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [availableLocations, eventLevelToken, orderBy, stringFilter]);

  return (
    <div className="row">
      <div className="col-sm-12">
        <div className="page-title-with-icon">
          <img
            src={`${config.WEB_BASE_URL}/images/icon/other-locations-icon.svg`}
            className="icon icon--medium"
          />
          <PageTitle
            title={i18next.t(
              "pages.locations-needing-attention.other-locations-header"
            )}
          />
        </div>
        <div className="op-content-box">
          <div className="event-type-filter">
            <div
              className={
                eventLevelToken === "needs-attention" ? "selected" : undefined
              }
              onClick={() => setEventLevelToken("needs-attention")}
            >
              <img
                src={`${config.WEB_BASE_URL}/images/icon/critical-event-filter-icon.svg`}
                className="icon icon--small"
              />
              {i18next.t(
                "pages.locations-needing-attention.event-type-needs-attention"
              )}
            </div>
            <div
              className={
                eventLevelToken === "locations_offline_count"
                  ? "selected"
                  : undefined
              }
              onClick={() => setEventLevelToken("locations_offline_count")}
            >
              <img
                src={`${config.WEB_BASE_URL}/images/icon/offline-event-filter-icon.svg`}
                className="icon icon--small"
              />
              {i18next.t(
                "pages.locations-needing-attention.event-type-offline"
              )}
            </div>
            <div
              className={
                eventLevelToken === "all-others" ? "selected" : undefined
              }
              onClick={() => setEventLevelToken("all-others")}
            >
              <img
                src={`${config.WEB_BASE_URL}/images/icon/check_icon.svg`}
                className="icon icon--small"
              />
              {i18next.t(
                "pages.locations-needing-attention.event-type-all-other"
              )}
            </div>
          </div>
          <div className="row location-filters">
            <div className="col-sm-4">
              <label>Order By</label>
              <Select
                options={[
                  { label: "Alphabetical A-Z", value: "alpha-asc" },
                  { label: "Alphabetical Z-A", value: "alpha-desc" },
                ]}
                isClearable={false}
                name="orderBy"
                value={orderBy}
                onChange={(choice) => setOrderBy(choice)}
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder={i18next.t("select")}
              />
            </div>
            <div className="col-sm-4 pull-right">
              <div className="col-sm-12">
                <input
                  type="text"
                  name="search"
                  autoComplete="off"
                  placeholder="Search"
                  className="location-name-filter"
                  onChange={(event) => {
                    setStringFilter(event.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="d-region">
            {isLoading ? (
              <div className="loader">{i18next.t("loading")}</div>
            ) : (
              <Masonry options={MASONRY_OPTIONS}>
                <div className="masonry-sizer" />
                <div className="gutter-sizer" />
                {locationReportData?.length > 0 &&
                  locationReportData.map((entry) => (
                    <div className="masonry-item" key={entry.location.value}>
                      <NeedsAttentionCard
                        location={entry.location}
                        counts={entry}
                        locationClicked={locationClicked}
                        needsAttention={
                          entry.criticalCount + entry.warningCount > 0
                        }
                      />
                    </div>
                  ))}
              </Masonry>
            )}
            {!locationReportData.length > 0 && (<div className="no-results">{i18next.t(
                  "pages.locations-needing-attention.no-locations-found"
                )}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherLocationsArea;
